// ./src/utils/api/EventVendorService.ts
//
// ROADMAP #170 (a) — vendor compliance: the vendors working an event, the documents each owes (insurance, permits…), the
// documents a vendor holds (reused by every event while current), their people (Contacts), and the vendor's own link.
// OpResult-native, event-scoped (owner, or crew with event.operate; plan: Pro and Business).
//
//   list / get / create / update / remove — the Vendors page; each read carries the whole picture (people, requests with
//     the document on each, counts, the documents still on file, the link).
//   setPeople — the vendor's people on this event, replaced as a whole (refusals keyed `people[<index>].<field>`).
//   sendLink / resetLink — email the link (the crew see a refusal), or replace it (the old one stops).
//   suggestions — vendors the event already uses (rentals, timeline, budget, partners) not on the list yet.
//   uploadFile / saveDocument — the crew put a document the vendor sent them on file.
//   requirement* — the documents asked for; review (approve / send back), reuseDocument (one on file).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { listAllPages } from "./listAllPages";

// Mirrors eventsmagic-api boundary/vendorCompliance.ts (`check-vendor-compliance` compares the copies).
export const VENDOR_DOCUMENT_TYPE = {
  INSURANCE: "insurance",
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  LICENSE: "license",
  OTHER: "other",
} as const;

export type VendorDocumentType = (typeof VENDOR_DOCUMENT_TYPE)[keyof typeof VENDOR_DOCUMENT_TYPE];

export const VENDOR_DOCUMENT_TYPE_LABEL: Record<VendorDocumentType, string> = {
  insurance: "Insurance",
  certificate: "Certificate",
  permit: "Permit",
  license: "Licence",
  other: "Other",
};

export const VENDOR_DOCUMENT_KIND_SUGGESTIONS: Record<VendorDocumentType, string[]> = {
  insurance: ["General liability", "Liquor liability", "Workers’ compensation", "Commercial auto", "Property / equipment", "Professional liability"],
  certificate: ["Food handler", "Food safety manager", "First aid / CPR", "Electrical", "Pyrotechnics", "Security guard"],
  permit: ["Temporary food service", "Special event", "Liquor", "Fire / open flame", "Tent / structure", "Noise", "Street use"],
  license: ["Business licence", "Liquor licence", "Vehicle / driver", "Music / performance rights"],
  other: [],
};

export const VENDOR_REQUIREMENT_STATUS = {
  MISSING: "missing",
  SUBMITTED: "submitted",
  APPROVED: "approved",
  RETURNED: "returned",
} as const;

export const VENDOR_LIMITS = {
  KIND_MAX: 128,
  REFERENCE_MAX: 128,
  ROLE_TITLE_MAX: 128,
  SERVICE_MAX: 256,
  NOTE_MAX: 2000,
  INSTRUCTIONS_MAX: 2000,
  FILES_MAX: 10,
  PEOPLE_MAX: 20,
  /** What one venue requires of its vendors (2026-10-20). */
  VENUE_REQUIREMENTS_MAX: 40,
  /** "Due N days before the event" — at most a year. */
  DUE_DAYS_BEFORE_MAX: 365,
} as const;

/** The largest file the server takes (it is sent as base64 inside the request). */
export const VENDOR_FILE_MAX_BYTES = Math.floor(3.5 * 1024 * 1024);

/** What a stored vendor file is (decided by the server from its bytes — a photo is always stored as a JPEG). */
export const VENDOR_FILE_MIME = { PDF: "application/pdf", JPEG: "image/jpeg", PNG: "image/png" } as const;

/** What a file input accepts — the server decides from the bytes, this only steers the picker. */
export const VENDOR_FILE_ACCEPT = "application/pdf,image/jpeg,image/png";

const UPLOADED_FILE = `uploadedFile { id fileUrl thumbnailUrl originalFilename mimeType sizeBytes }`;

const DOCUMENT_FIELDS = `
  id
  vendorId
  documentType
  documentKind
  name
  referenceNo
  issuedOn
  expiresOn
  notes
  submittedVia
  createdAt
  expiresBeforeEvent
  files { id uploadedFileId ${UPLOADED_FILE} }
`;

const REQUIREMENT_FIELDS = `
  id
  eventId
  eventVendorId
  documentType
  documentKind
  name
  instructions
  dueDate
  vendorDocumentId
  venueId
  venueName
  reviewStatus
  reviewNote
  submittedAt
  reviewedAt
  orderNo
  attention
  document { ${DOCUMENT_FIELDS} }
  rights
`;

const EVENT_VENDOR_FIELDS = `
  id
  accountId
  eventId
  eventTitle
  eventStartsAt
  eventTimezone
  vendorId
  vendorName
  vendorEmail
  vendorPhone
  vendorRemoved
  serviceDescription
  notes
  autoReminders
  linkSentAt
  vendorUrl
  orderNo
  people { personId firstName lastName email phone roleTitle receivesEmails }
  vendorPeople { personId firstName lastName email phone roleTitle receivesEmails }
  requirements { ${REQUIREMENT_FIELDS} }
  counts { total missing submitted approved returned attention }
  documentsOnFile { ${DOCUMENT_FIELDS} }
  venueIds
  workVenues { id name }
  eventVenues { id name }
  createdAt
  updatedAt
  status
  rights
`;

const RESULT = (fields: string, withTotal = false) => `
  code
  ${withTotal ? "total" : ""}
  data { ${fields} }
  errors { name errors warnings }
`;

const EVENT_VENDOR_LIST_QUERY = gql`
  query EventVendorList($filter: EventVendorFilter, $params: PaginationAndSorting) {
    eventVendorList(filter: $filter, params: $params) { ${RESULT(EVENT_VENDOR_FIELDS, true)} }
  }
`;

const EVENT_VENDOR_GET_QUERY = gql`
  query EventVendorGet($id: ID) {
    eventVendorGet(id: $id) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_SUGGESTIONS_QUERY = gql`
  query EventVendorSuggestions($params: EventVendorSuggestionsInput) {
    eventVendorSuggestions(params: $params) { ${RESULT("vendorId vendorName foundIn")} }
  }
`;

const EVENT_VENDOR_VENUE_OPTIONS_QUERY = gql`
  query EventVendorVenueOptions($params: EventVendorSuggestionsInput) {
    eventVendorVenueOptions(params: $params) { ${RESULT("id name")} }
  }
`;

const EVENT_VENDOR_APPLY_VENUE_REQUIREMENTS_MUTATION = gql`
  mutation EventVendorApplyVenueRequirements($params: EventVendorIdInput) {
    eventVendorApplyVenueRequirements(params: $params) { ${RESULT(`${EVENT_VENDOR_FIELDS} requirementsAdded`)} }
  }
`;

const EVENT_VENDOR_CREATE_MUTATION = gql`
  mutation EventVendorCreate($params: EventVendorInput) {
    eventVendorCreate(params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_UPDATE_MUTATION = gql`
  mutation EventVendorUpdate($where: EventVendorWhereInput, $params: EventVendorInput) {
    eventVendorUpdate(where: $where, params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_REMOVE_MUTATION = gql`
  mutation EventVendorRemove($where: EventVendorWhereInput) {
    eventVendorRemove(where: $where) { ${RESULT("id")} }
  }
`;

const EVENT_VENDOR_SET_PEOPLE_MUTATION = gql`
  mutation EventVendorSetPeople($params: EventVendorSetPeopleInput) {
    eventVendorSetPeople(params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_SEND_LINK_MUTATION = gql`
  mutation EventVendorSendLink($params: EventVendorIdInput) {
    eventVendorSendLink(params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_RESET_LINK_MUTATION = gql`
  mutation EventVendorResetLink($params: EventVendorIdInput) {
    eventVendorResetLink(params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const EVENT_VENDOR_FILE_UPLOAD_MUTATION = gql`
  mutation EventVendorFileUpload($params: EventVendorFileUploadInput) {
    eventVendorFileUpload(params: $params) { ${RESULT(`uploadedFileId ${UPLOADED_FILE}`)} }
  }
`;

const EVENT_VENDOR_DOCUMENT_SAVE_MUTATION = gql`
  mutation EventVendorDocumentSave($params: EventVendorDocumentSaveInput) {
    eventVendorDocumentSave(params: $params) { ${RESULT(EVENT_VENDOR_FIELDS)} }
  }
`;

const REQUIREMENT_CREATE_MUTATION = gql`
  mutation EventVendorRequirementCreate($params: EventVendorRequirementInput) {
    eventVendorRequirementCreate(params: $params) { ${RESULT(REQUIREMENT_FIELDS)} }
  }
`;

const REQUIREMENT_UPDATE_MUTATION = gql`
  mutation EventVendorRequirementUpdate($where: EventVendorRequirementWhereInput, $params: EventVendorRequirementInput) {
    eventVendorRequirementUpdate(where: $where, params: $params) { ${RESULT(REQUIREMENT_FIELDS)} }
  }
`;

const REQUIREMENT_REMOVE_MUTATION = gql`
  mutation EventVendorRequirementRemove($where: EventVendorRequirementWhereInput) {
    eventVendorRequirementRemove(where: $where) { ${RESULT("id")} }
  }
`;

const REQUIREMENT_REVIEW_MUTATION = gql`
  mutation EventVendorRequirementReview($params: EventVendorRequirementReviewInput) {
    eventVendorRequirementReview(params: $params) { ${RESULT(REQUIREMENT_FIELDS)} }
  }
`;

const REQUIREMENT_USE_DOCUMENT_MUTATION = gql`
  mutation EventVendorRequirementUseDocument($params: EventVendorRequirementUseDocumentInput) {
    eventVendorRequirementUseDocument(params: $params) { ${RESULT(REQUIREMENT_FIELDS)} }
  }
`;

export interface UploadedFileRef {
  id?: string;
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
  originalFilename?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
}

export interface VendorDocument {
  id?: string;
  vendorId?: string;
  documentType?: string;
  documentKind?: string | null;
  /** "Insurance · General liability". */
  name?: string;
  referenceNo?: string | null;
  /** YYYY-MM-DD. */
  issuedOn?: string | null;
  /** YYYY-MM-DD — the last day it is valid; null = does not expire. */
  expiresOn?: string | null;
  notes?: string | null;
  submittedVia?: string | null;
  createdAt?: string | null;
  expiresBeforeEvent?: boolean | null;
  files?: Array<{ id?: string; uploadedFileId?: string; uploadedFile?: UploadedFileRef | null }>;
  /** Library reads only (the account's own documents). */
  rights?: string[];
}

export interface EventVendorRequirement {
  id?: string;
  eventId?: string;
  eventVendorId?: string;
  documentType?: string;
  documentKind?: string | null;
  name?: string;
  instructions?: string | null;
  /** YYYY-MM-DD. */
  dueDate?: string | null;
  vendorDocumentId?: string | null;
  /** The venue whose requirement added it (2026-10-20); null = asked by the crew. */
  venueId?: string | null;
  venueName?: string | null;
  reviewStatus?: string;
  reviewNote?: string | null;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  orderNo?: number | null;
  /** 'overdue' | 'expires' | null. */
  attention?: string | null;
  document?: VendorDocument | null;
  rights?: string[];
}

export interface EventVendorPerson {
  personId?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  roleTitle?: string | null;
  receivesEmails?: boolean | null;
}

export interface EventVendorCounts {
  total: number;
  missing: number;
  submitted: number;
  approved: number;
  returned: number;
  attention: number;
}

export interface EventVendor {
  id?: string;
  accountId?: string;
  eventId?: string;
  eventTitle?: string | null;
  eventStartsAt?: string | null;
  eventTimezone?: string | null;
  vendorId?: string;
  vendorName?: string | null;
  vendorEmail?: string | null;
  vendorPhone?: string | null;
  vendorRemoved?: boolean | null;
  serviceDescription?: string | null;
  notes?: string | null;
  autoReminders?: boolean | null;
  linkSentAt?: string | null;
  vendorUrl?: string | null;
  orderNo?: number | null;
  people?: EventVendorPerson[];
  /** Everyone the vendor has, with their role — what the people drawer offers. */
  vendorPeople?: EventVendorPerson[];
  requirements?: EventVendorRequirement[];
  counts?: EventVendorCounts | null;
  documentsOnFile?: VendorDocument[];
  /** #170 (2026-10-20) — the venues chosen for it, of the event's; empty = the event's own venue. */
  venueIds?: string[];
  /** Where it works — the chosen venues, else the event's own: whose requirements it is asked for. */
  workVenues?: EventVendorVenue[];
  /** The event's venues (its own, then its sessions') — the choices for `venueIds`. */
  eventVenues?: EventVendorVenue[];
  /** Only from `applyVenueRequirements`: how many requests it added. */
  requirementsAdded?: number | null;
  createdAt?: string;
  updatedAt?: string;
  status?: number;
  rights?: string[];
}

export interface EventVendorInput {
  eventId?: string;
  vendorId?: string;
  serviceDescription?: string | null;
  notes?: string | null;
  autoReminders?: boolean;
  orderNo?: number;
  /** Which of the event's venues it works at — the WHOLE list (omit to leave it). */
  venueIds?: string[];
}

export interface EventVendorFilter {
  id?: string[];
  eventId?: string[];
  vendorId?: string[];
  searchKeyword?: string;
}

export interface EventVendorPersonInput {
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  roleTitle?: string | null;
  receivesEmails?: boolean;
}

export interface EventVendorSuggestion {
  vendorId: string;
  vendorName: string;
  foundIn: string[];
}

export interface VendorDocumentInput {
  requirementId?: string | null;
  documentType: string;
  documentKind?: string | null;
  referenceNo?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
  fileIds: string[];
}

/** #170 (2026-10-20) — one thing a VENUE requires of the vendors working there (saved with the venue). */
export interface VenueRequirement {
  id?: string;
  venueId?: string;
  /** Only vendors of this (service) category; null = every vendor. */
  venueCategoryId?: string | null;
  documentType?: string;
  documentKind?: string | null;
  name?: string;
  instructions?: string | null;
  /** Due this many days before the event's first day; null = no due date. */
  dueDaysBefore?: number | null;
  orderNo?: number | null;
}

export interface VenueRequirementInput {
  id?: string;
  venueCategoryId: string | null;
  documentType: string;
  documentKind: string | null;
  instructions: string | null;
  dueDaysBefore: number | null;
}

/** One of an event's venues (its own, or a session's). */
export interface EventVendorVenue {
  id?: string;
  name?: string | null;
}

export interface EventVendorRequirementInput {
  eventVendorId?: string;
  documentType?: string;
  documentKind?: string | null;
  instructions?: string | null;
  dueDate?: string | null;
  orderNo?: number;
}

export class EventVendorService {
  static async list(filter?: EventVendorFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_VENDOR_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_VENDOR_GET_QUERY, { id });
  }

  static async suggestions(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_VENDOR_SUGGESTIONS_QUERY, { params: { eventId } });
  }

  /** The event's venues (its own, its sessions') — where a vendor can be said to work. */
  static async venueOptions(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_VENDOR_VENUE_OPTIONS_QUERY, { params: { eventId } });
  }

  /** "Add the venue's requirements" — answers the vendor with `requirementsAdded`. */
  static async applyVenueRequirements(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_APPLY_VENUE_REQUIREMENTS_MUTATION, { params: { id } });
  }

  static async create(params: EventVendorInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_CREATE_MUTATION, { params });
  }

  static async update(id: string, params: EventVendorInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_REMOVE_MUTATION, { where: { id } });
  }

  /** The whole list, replaced; refusals keyed `people[<index>].<field>`. */
  static async setPeople(eventVendorId: string, people: EventVendorPersonInput[]): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_SET_PEOPLE_MUTATION, { params: { eventVendorId, people } });
  }

  static async sendLink(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_SEND_LINK_MUTATION, { params: { id } });
  }

  static async resetLink(id: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_RESET_LINK_MUTATION, { params: { id } });
  }

  /** One file (a PDF, JPEG or PNG), base64 — kept pending until a document is saved with it. */
  static async uploadFile(eventVendorId: string, fileName: string, dataBase64: string): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_FILE_UPLOAD_MUTATION, { params: { eventVendorId, fileName, dataBase64 } });
  }

  static async saveDocument(eventVendorId: string, input: VendorDocumentInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_VENDOR_DOCUMENT_SAVE_MUTATION, { params: { eventVendorId, ...input } });
  }

  static async createRequirement(params: EventVendorRequirementInput): Promise<OpResult> {
    return ApiService.executeMutation(REQUIREMENT_CREATE_MUTATION, { params });
  }

  static async updateRequirement(id: string, params: EventVendorRequirementInput): Promise<OpResult> {
    return ApiService.executeMutation(REQUIREMENT_UPDATE_MUTATION, { where: { id }, params });
  }

  static async removeRequirement(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REQUIREMENT_REMOVE_MUTATION, { where: { id } });
  }

  /** Approve the document on a request, or send it back with a note (the vendor sees it); `expiresOn` corrects the date. */
  static async reviewRequirement(id: string, decision: "approve" | "return", note?: string | null, expiresOn?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(REQUIREMENT_REVIEW_MUTATION, {
      params: { id, decision, note: note ?? null, ...(expiresOn !== undefined ? { expiresOn } : {}) },
    });
  }

  /** Answer a request with a document already on the vendor's file (it then waits for review). */
  static async reuseDocument(id: string, vendorDocumentId: string): Promise<OpResult> {
    return ApiService.executeMutation(REQUIREMENT_USE_DOCUMENT_MUTATION, { params: { id, vendorDocumentId } });
  }
}

/** "Insurance · General liability" for a type and kind (the server's own `name`, for a form that has not saved yet). */
export const vendorDocumentName = (type?: string | null, kind?: string | null): string => {
  const label = VENDOR_DOCUMENT_TYPE_LABEL[(type ?? "") as VendorDocumentType] ?? "Document";
  const k = (kind ?? "").trim();

  return k ? `${label} · ${k}` : label;
};

/** A file read as base64 (no `data:` prefix) for an upload. */
export const fileToBase64 = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? "").replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error ?? new Error("The file could not be read"));
    reader.readAsDataURL(file);
  });

// ─── Library › Vendors: a vendor's people and the documents it holds (the account's own) ─────────────────────────────

const VENDOR_PERSON_FIELDS = `id accountId vendorId personId roleTitle firstName lastName email phone orderNo status rights`;

const VENDOR_PERSON_LIST_QUERY = gql`
  query VendorPersonList($filter: VendorPersonFilter, $params: PaginationAndSorting) {
    vendorPersonList(filter: $filter, params: $params) { ${RESULT(VENDOR_PERSON_FIELDS, true)} }
  }
`;

const VENDOR_PERSON_CREATE_MUTATION = gql`
  mutation VendorPersonCreate($params: VendorPersonInput) {
    vendorPersonCreate(params: $params) { ${RESULT(VENDOR_PERSON_FIELDS)} }
  }
`;

const VENDOR_PERSON_UPDATE_MUTATION = gql`
  mutation VendorPersonUpdate($where: VendorPersonWhereInput, $params: VendorPersonInput) {
    vendorPersonUpdate(where: $where, params: $params) { ${RESULT(VENDOR_PERSON_FIELDS)} }
  }
`;

const VENDOR_PERSON_REMOVE_MUTATION = gql`
  mutation VendorPersonRemove($where: VendorPersonWhereInput) {
    vendorPersonRemove(where: $where) { ${RESULT("id")} }
  }
`;

const VENDOR_DOCUMENT_LIST_QUERY = gql`
  query VendorDocumentList($filter: VendorDocumentFilter, $params: PaginationAndSorting) {
    vendorDocumentList(filter: $filter, params: $params) { ${RESULT(`${DOCUMENT_FIELDS} rights`, true)} }
  }
`;

const VENDOR_DOCUMENT_GET_QUERY = gql`
  query VendorDocumentGet($id: ID) {
    vendorDocumentGet(id: $id) { ${RESULT(`${DOCUMENT_FIELDS} rights`)} }
  }
`;

const VENDOR_DOCUMENT_UPDATE_MUTATION = gql`
  mutation VendorDocumentUpdate($where: VendorDocumentWhereInput, $params: VendorDocumentInput) {
    vendorDocumentUpdate(where: $where, params: $params) { ${RESULT(`${DOCUMENT_FIELDS} rights`)} }
  }
`;

const VENDOR_DOCUMENT_FILE_UPLOAD_MUTATION = gql`
  mutation VendorDocumentFileUpload($params: VendorDocumentFileUploadInput) {
    vendorDocumentFileUpload(params: $params) { ${RESULT(`uploadedFileId ${UPLOADED_FILE}`)} }
  }
`;

const VENDOR_DOCUMENT_ADD_MUTATION = gql`
  mutation VendorDocumentAdd($params: VendorDocumentAddInput) {
    vendorDocumentAdd(params: $params) { ${RESULT(`${DOCUMENT_FIELDS} rights`)} }
  }
`;

const VENDOR_DOCUMENT_REMOVE_MUTATION = gql`
  mutation VendorDocumentRemove($where: VendorDocumentWhereInput) {
    vendorDocumentRemove(where: $where) { ${RESULT("id")} }
  }
`;

/** One of a vendor's people (Library › Vendors) — the person's CURRENT name and contacts, with their role there. */
export interface VendorPerson {
  id?: string;
  accountId?: string;
  vendorId?: string;
  personId?: string;
  roleTitle?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[];
}

export interface VendorPersonInput {
  vendorId?: string;
  /** A contact from Contacts — or a new one by name and address (found first, never overwritten). */
  personId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  roleTitle?: string | null;
}

/** What crew correct on a document on file (its files and type stay — another document is a new one). */
export interface VendorDocumentEditInput {
  documentKind?: string | null;
  referenceNo?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  notes?: string | null;
}

export class VendorLibraryService {
  static async listPeople(filter: { vendorId: string[] }, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(VENDOR_PERSON_LIST_QUERY, { filter, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } });
  }

  /** Every person the vendor has — the whole set, in one read (a vendor's people are few; the page shows them all). */
  static async allPeople(vendorId: string): Promise<OpResult> {
    return listAllPages<VendorPerson>(VendorLibraryService.listPeople, { vendorId: [vendorId] });
  }

  static async addPerson(params: VendorPersonInput): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_PERSON_CREATE_MUTATION, { params });
  }

  static async updatePerson(id: string, params: Pick<VendorPersonInput, "roleTitle">): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_PERSON_UPDATE_MUTATION, { where: { id }, params });
  }

  static async removePerson(id: string): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_PERSON_REMOVE_MUTATION, { where: { id } });
  }

  static async listDocuments(filter: { vendorId: string[] }, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(VENDOR_DOCUMENT_LIST_QUERY, { filter, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } });
  }

  /** One document by id — a stage opened for a document not on the loaded page (the account's own; null otherwise). */
  static async getDocument(id: string): Promise<OpResult> {
    return ApiService.executeQuery(VENDOR_DOCUMENT_GET_QUERY, { id });
  }

  /** One file for a document added on the vendor's own page — kept pending until the document is added. */
  static async uploadFile(vendorId: string, fileName: string, dataBase64: string): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_DOCUMENT_FILE_UPLOAD_MUTATION, { params: { vendorId, fileName, dataBase64 } });
  }

  /**
   * Put a document on the vendor's file from its own page (no event, so no request: `requirementId` is not sent — the
   * server's input does not take one).
   */
  static async addDocument(vendorId: string, input: VendorDocumentInput): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_DOCUMENT_ADD_MUTATION, {
      params: {
        vendorId,
        documentType: input.documentType,
        documentKind: input.documentKind,
        referenceNo: input.referenceNo,
        issuedOn: input.issuedOn,
        expiresOn: input.expiresOn,
        notes: input.notes,
        fileIds: input.fileIds,
      },
    });
  }

  static async updateDocument(id: string, params: VendorDocumentEditInput): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_DOCUMENT_UPDATE_MUTATION, { where: { id }, params });
  }

  /** Off the vendor's file; every request it answered goes back to "not received". */
  static async removeDocument(id: string): Promise<OpResult> {
    return ApiService.executeMutation(VENDOR_DOCUMENT_REMOVE_MUTATION, { where: { id } });
  }
}
